import { type ApolloLink, ApolloClient, HttpLink, InMemoryCache, split } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { getMainDefinition } from '@apollo/client/utilities'
import { IncomingHttpHeaders } from 'http'

import { GRAPHQL_URL, inMemoryCacheConfig, NEXT_PUBLIC_GRAPHQL_URL } from '../constants'

export type IncomingMessageWithHeaders = { headers: Headers | IncomingHttpHeaders }

declare module 'http' {
  interface IncomingHttpHeaders {
    'cf-connecting-ip'?: string
    'cf-verified-bot'?: string
  }
}

export type ApolloClientOptions = {
  csrUri?: string
  ssrUri?: string
}

const isServer = typeof window === 'undefined'

const clientHeadersOnlyToForward = ['cf-connecting-ip'] as const
const headersToForward = [
  'cf-verified-bot',
  'cookie',
  'user-agent',
  ...(isServer ? [] : clientHeadersOnlyToForward),
] as const

const getApolloUrl = (options: ApolloClientOptions, useBatch = false) => {
  if (isServer) {
    return options.ssrUri || GRAPHQL_URL
  }

  if (useBatch) {
    return `${options.csrUri || NEXT_PUBLIC_GRAPHQL_URL}/apollo-batch`
  }

  return options.csrUri || NEXT_PUBLIC_GRAPHQL_URL
}

/**
 * Checks if the operation is a mutation.
 * Used to split mutations from batching to avoid race conditions.
 */
const isMutation = ({ query }: { query: Parameters<typeof getMainDefinition>[0] }) => {
  const definition = getMainDefinition(query)
  return definition.kind === 'OperationDefinition' && definition.operation === 'mutation'
}

/**
 * Creates the appropriate Apollo Link based on the environment.
 *
 * On CSR: Uses split() to route mutations to HttpLink (no batching) and
 * queries to BatchHttpLink. This prevents race conditions where mutations
 * could return stale data if batched with queries that fetch the same fields.
 *
 * On SSR: Uses a simple HttpLink (no batching needed server-side).
 */
function createLink(
  forwardedHeaders: Record<string, string>,
  options: ApolloClientOptions
): ApolloLink {
  const linkOptions = {
    credentials: 'include' as const,
    headers: {
      ...forwardedHeaders,
      accept: 'application/json',
      'content-type': 'application/json',
    },
  }

  if (isServer) {
    return new HttpLink({
      ...linkOptions,
      uri: getApolloUrl(options),
    })
  }

  const httpLink = new HttpLink({
    ...linkOptions,
    uri: getApolloUrl(options),
  })

  const batchLink = new BatchHttpLink({
    ...linkOptions,
    uri: getApolloUrl(options, true),
    batchMax: 10,
    batchInterval: 10,
  })

  return split(isMutation, httpLink, batchLink)
}

export function createApolloClient(
  req?: IncomingMessageWithHeaders,
  options: ApolloClientOptions = {}
) {
  const forwardedHeaders = req?.headers
    ? headersToForward.reduce<Record<string, string>>((acc, name) => {
        const value = req.headers instanceof Headers ? req.headers.get(name) : req.headers[name]
        if (value) acc[name] = value
        return acc
      }, {})
    : {}

  return new ApolloClient({
    ssrMode: isServer,
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
    link: createLink(forwardedHeaders, options),
    cache: new InMemoryCache(inMemoryCacheConfig),
  })
}
